import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, type AuthConfig } from '@/lib/api'
import { loginWithLark, loginWithPassword } from '@/lib/auth-client'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Lark 未配置时不渲染那个入口，避免点了才发现没用
  const { data: config } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/auth-config'),
  })

  const login = useMutation({
    mutationFn: () => loginWithPassword(username.trim().toLowerCase(), password),
    onSuccess: (result) => {
      if (result.error) {
        setError(result.error)
        return
      }
      window.location.href = '/dashboard'
    },
    onError: () => setError('登录失败，请稍后重试。'),
  })

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      {/* 登录页在 dashboard 外壳之外，自带一个切换入口——否则偏好暗色的人
          要先登进去才能换。绝对定位，不打扰居中的卡片。 */}
      <div className="absolute top-5 right-5">
        <ThemeToggle />
      </div>

      <div className="rise w-full max-w-[380px]">
        {/* 顶部光条：ignix 的视觉母题，尺度收细以贴合 Apple 的克制。
            浅底去掉外发光——白底上的辉光只会显脏，改用两端更长的淡出保持轻盈。 */}
        <div
          aria-hidden
          className="mx-auto h-[2px] w-28 rounded-full"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgb(var(--cyan) / .75) 50%, transparent)',
          }}
        />

        <div className="surface mt-8 rounded-apple-xl p-8 shadow-apple-lg">
          <div className="text-center">
            <h1 className="display-title text-[34px] leading-none text-txt">Launchpad</h1>
            <p className="mt-2.5 font-mono text-[11px] tracking-[0.14em] text-mut2">运营控制台</p>
          </div>

          <form
            className="mt-8 space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              setError(null)
              if (!username.trim() || !password) {
                setError('请填写账号和密码。')
                return
              }
              login.mutate()
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-[12.5px] text-mut">
                账号
              </Label>
              <Input
                id="username"
                value={username}
                autoComplete="username"
                onChange={(e) => setUsername(e.target.value)}
                placeholder="用户名"
                className="h-10 rounded-apple text-[13.5px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12.5px] text-mut">
                密码
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 rounded-apple text-[13.5px]"
              />
            </div>

            {error && (
              <p role="alert" className="text-[12.5px] text-rose">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="pressable h-10 w-full rounded-apple text-[13.5px]"
              disabled={login.isPending}
            >
              {login.isPending ? '登录中…' : '登录'}
            </Button>
          </form>

          {config?.larkEnabled && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-line/60" />
                <span className="font-mono text-[10px] tracking-[0.18em] text-dim">OR</span>
                <div className="h-px flex-1 bg-line/60" />
              </div>

              {/* Lark 是次要入口：账号密码在上方作为主操作 */}
              <Button
                variant="outline"
                className="pressable h-10 w-full rounded-apple text-[13.5px]"
                onClick={() => loginWithLark()}
              >
                使用 Lark 登录
              </Button>
            </>
          )}
        </div>

        {/* --dim 是装饰级灰度（约 3.4:1），这句是有实义的告知，用 --mut2 保证可读 */}
        <p className="mt-6 text-center text-[11px] text-mut2">
          仅限授权人员访问，所有操作均会记入审计日志
        </p>
      </div>
    </div>
  )
}
